import { createElement, useSyncExternalStore, type CSSProperties, type ReactNode } from 'react';

type SxValue = string | number | boolean | null | undefined | SxObject;
export interface SxObject {
    [key: string]: SxValue;
}

/** 淡色底與深色字都由主題色混出來，深色主題下才不會變成白底配白字 */
function tintOf(token: string, percent: number) {
    return `color-mix(in oklab, var(--color-${token}) ${percent}%, var(--color-base-100))`;
}

function shadeOf(token: string) {
    return `color-mix(in oklab, var(--color-${token}) 55%, var(--color-base-content))`;
}

/** MUI 調色盤名稱對應到 daisyUI 主題變數，頁面顏色才會跟著淺色／深色主題切換 */
const COLOR_MAP: Record<string, string> = {
    'background.default': 'var(--color-base-100)',
    'background.paper': 'var(--color-base-100)',
    'text.primary': 'var(--color-base-content)',
    'text.secondary': 'var(--text-secondary)',
    'text.disabled': 'var(--text-muted)',
    'primary.main': 'var(--color-primary)',
    'primary.contrastText': 'var(--color-primary-content)',
    'primary.light': tintOf('primary', 70),
    'primary.lighter': tintOf('primary', 14),
    'primary.50': tintOf('primary', 8),
    'primary.100': tintOf('primary', 16),
    'primary.dark': shadeOf('primary'),
    'secondary.main': 'var(--color-accent)',
    'error.main': 'var(--color-error)',
    'error.light': tintOf('error', 22),
    'error.dark': shadeOf('error'),
    'warning.main': 'var(--color-warning)',
    'warning.dark': shadeOf('warning'),
    'warning.light': tintOf('warning', 28),
    'warning.lighter': tintOf('warning', 14),
    'success.main': 'var(--color-success)',
    'success.dark': shadeOf('success'),
    'success.light': tintOf('success', 22),
    'success.lighter': tintOf('success', 12),
    'info.main': 'var(--color-info)',
    'info.light': tintOf('info', 22),
    'info.lighter': tintOf('info', 12),
    'grey.50': 'var(--color-base-100)',
    'grey.100': 'var(--color-base-200)',
    'grey.200': 'var(--color-base-300)',
    'grey.300': 'color-mix(in oklab, var(--color-base-content) 20%, transparent)',
    'grey.400': 'color-mix(in oklab, var(--color-base-content) 35%, transparent)',
    'grey.500': 'var(--text-muted)',
    divider: 'color-mix(in oklab, var(--color-base-content) 16%, transparent)',
    inherit: 'inherit',
};

const SPACING_PROPS = new Set([
    'm',
    'mt',
    'mr',
    'mb',
    'ml',
    'mx',
    'my',
    'p',
    'pt',
    'pr',
    'pb',
    'pl',
    'px',
    'py',
    'gap',
    'rowGap',
    'columnGap',
]);

const DIRECT_PROPS = new Set([
    'display',
    'flex',
    'position',
    'top',
    'right',
    'bottom',
    'left',
    'width',
    'height',
    'minWidth',
    'minHeight',
    'maxWidth',
    'maxHeight',
    'overflow',
    'overflowX',
    'overflowY',
    'opacity',
    'cursor',
    'zIndex',
    'textAlign',
    'lineHeight',
    'whiteSpace',
    'fontSize',
    'fontWeight',
    'fontFamily',
    'letterSpacing',
    'alignItems',
    'justifyContent',
    'flexDirection',
    'flexWrap',
    'flexGrow',
    'flexShrink',
    'gridTemplateColumns',
    'gridTemplateRows',
    'gridColumn',
    'gridRow',
    'alignSelf',
    'justifySelf',
    'objectFit',
    'transform',
]);

const STYLE_PROP_KEYS = new Set([
    ...SPACING_PROPS,
    ...DIRECT_PROPS,
    'bgcolor',
    'backgroundColor',
    'color',
    'border',
    'borderTop',
    'borderRight',
    'borderBottom',
    'borderLeft',
    'borderColor',
    'borderRadius',
    'boxShadow',
]);

function toCssUnit(value: SxValue): string | number | undefined {
    if (typeof value === 'number') {
        return `${value * 8}px`;
    }
    if (typeof value === 'string') {
        return COLOR_MAP[value] || value;
    }
    return undefined;
}

/** 這些屬性的數字就是 CSS 像素（MUI 語意），不能套用 8px 的間距單位 */
const PIXEL_PROPS = new Set([
    'width',
    'height',
    'minWidth',
    'minHeight',
    'maxWidth',
    'maxHeight',
    'top',
    'right',
    'bottom',
    'left',
    'fontSize',
    'letterSpacing',
]);

/** 尺寸類屬性 0～1 之間的數字代表佔父層的比例，與 MUI 一致 */
const FRACTION_SIZE_PROPS = new Set([
    'width',
    'height',
    'minWidth',
    'minHeight',
    'maxWidth',
    'maxHeight',
]);

function resolveDirectValue(key: string, value: SxValue): string | number | undefined {
    if (typeof value !== 'number') {
        return toCssUnit(value);
    }
    if (FRACTION_SIZE_PROPS.has(key) && value > 0 && value <= 1) {
        return `${value * 100}%`;
    }
    if (PIXEL_PROPS.has(key)) {
        return `${value}px`;
    }
    return value;
}

/** 斷點的最小寬度，與 MUI 預設一致；由大到小排列，取第一個符合目前視窗寬度的 */
const BREAKPOINTS_DESCENDING = [
    ['xl', 1536],
    ['lg', 1200],
    ['md', 900],
    ['sm', 600],
    ['xs', 0],
] as const;

function currentBreakpoint(): string {
    if (typeof window === 'undefined') return 'xs';
    const width = window.innerWidth;
    return BREAKPOINTS_DESCENDING.find(([, minWidth]) => width >= minWidth)?.[0] ?? 'xs';
}

function subscribeToBreakpointChange(onChange: () => void) {
    window.addEventListener('resize', onChange);
    return () => window.removeEventListener('resize', onChange);
}

/** 讓使用 { xs, md ... } 這類響應式 sx 值的元件，在視窗跨過斷點時重新繪製 */
export function useBreakpoint(): string {
    return useSyncExternalStore(subscribeToBreakpointChange, currentBreakpoint, () => 'xs');
}

function resolveResponsiveValue(value: SxValue): SxValue {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return value;
    }

    const responsive = value as SxObject;
    const active = currentBreakpoint();
    const activeIndex = BREAKPOINTS_DESCENDING.findIndex(([name]) => name === active);
    // 沒有定義目前斷點時，沿用比它小的最近一個斷點（mobile-first）
    for (const [name] of BREAKPOINTS_DESCENDING.slice(activeIndex)) {
        if (responsive[name] !== undefined) return responsive[name];
    }
    return Object.values(responsive).find((item) => item !== undefined);
}

function assignSpacing(style: CSSProperties, key: string, value: SxValue) {
    const resolved = toCssUnit(resolveResponsiveValue(value));
    if (resolved === undefined) return;

    const assignments: Record<string, string[]> = {
        m: ['margin'],
        mt: ['marginTop'],
        mr: ['marginRight'],
        mb: ['marginBottom'],
        ml: ['marginLeft'],
        mx: ['marginLeft', 'marginRight'],
        my: ['marginTop', 'marginBottom'],
        p: ['padding'],
        pt: ['paddingTop'],
        pr: ['paddingRight'],
        pb: ['paddingBottom'],
        pl: ['paddingLeft'],
        px: ['paddingLeft', 'paddingRight'],
        py: ['paddingTop', 'paddingBottom'],
        gap: ['gap'],
        rowGap: ['rowGap'],
        columnGap: ['columnGap'],
    };

    for (const prop of assignments[key] || []) {
        (style as Record<string, string | number>)[prop] = resolved;
    }
}

function assignBorder(style: CSSProperties, key: string, value: SxValue) {
    const resolved = resolveResponsiveValue(value);
    if (resolved === undefined || resolved === null || resolved === false) return;

    const borderProps: Record<string, string> = {
        border: 'border',
        borderTop: 'borderTop',
        borderRight: 'borderRight',
        borderBottom: 'borderBottom',
        borderLeft: 'borderLeft',
        borderRadius: 'borderRadius',
    };

    if (key === 'borderColor') {
        style.borderColor = toCssUnit(resolved) as string;
        return;
    }

    if (key === 'borderRadius') {
        style.borderRadius = typeof resolved === 'number' ? `${resolved * 4}px` : String(resolved);
        return;
    }

    const cssKey = borderProps[key];
    if (!cssKey) return;

    (style as Record<string, string | number>)[cssKey] =
        typeof resolved === 'number'
            ? `${resolved}px solid ${COLOR_MAP.divider}`
            : String(resolved);
}

export function sxToStyle(sxProp?: SxObject): CSSProperties {
    if (!sxProp) return {};

    const style: CSSProperties = {};

    for (const [rawKey, rawValue] of Object.entries(sxProp)) {
        const key = rawKey.trim();
        const value = resolveResponsiveValue(rawValue);

        if (value === undefined || value === null || value === false) continue;
        if (key.startsWith('&') || key.startsWith('@')) continue;

        if (SPACING_PROPS.has(key)) {
            assignSpacing(style, key, value);
            continue;
        }

        if (key === 'bgcolor' || key === 'backgroundColor') {
            style.backgroundColor = toCssUnit(value) as string;
            continue;
        }

        if (key === 'color') {
            style.color = toCssUnit(value) as string;
            continue;
        }

        if (key === 'fontWeight') {
            style.fontWeight =
                value === 'medium' ? 500 : value === 'regular' ? 400 : (value as string | number);
            continue;
        }

        if (key.startsWith('border')) {
            assignBorder(style, key, value);
            continue;
        }

        if (key === 'boxShadow') {
            style.boxShadow =
                typeof value === 'number'
                    ? `0 ${Math.max(value, 1)}px ${Math.max(value * 4, 4)}px rgba(15, 23, 42, 0.12)`
                    : String(value);
            continue;
        }

        if (DIRECT_PROPS.has(key)) {
            (style as Record<string, string | number>)[key] = resolveDirectValue(key, value) as
                string | number;
        }
    }

    return style;
}

export function cx(...parts: Array<string | false | null | undefined>) {
    return parts.filter(Boolean).join(' ');
}

export function splitStyleProps<T extends Record<string, unknown>>(props: T, sxProp?: SxObject) {
    const domProps: Record<string, unknown> = {};
    const styleProps: SxObject = { ...(sxProp || {}) };

    for (const [key, value] of Object.entries(props)) {
        if (STYLE_PROP_KEYS.has(key)) {
            styleProps[key] = value as SxValue;
        } else {
            domProps[key] = value;
        }
    }

    return { domProps, style: sxToStyle(styleProps) };
}

export function renderAdornment(adornment: ReactNode) {
    if (!adornment) return null;
    return createElement(
        'span',
        { className: 'flex items-center text-base-content/60' },
        adornment
    );
}

export function toArray<T>(value: T | T[] | null | undefined): T[] {
    if (Array.isArray(value)) return value;
    if (value === null || value === undefined) return [];
    return [value];
}

export function toneToButtonClass(tone?: string) {
    switch (tone) {
        case 'secondary':
        case 'warning':
            return 'btn-warning';
        case 'error':
            return 'btn-error';
        case 'success':
            return 'btn-success';
        case 'info':
            return 'btn-info';
        case 'inherit':
            return 'btn-ghost';
        default:
            return 'btn-primary';
    }
}

export function toneToAlertClass(tone?: string) {
    switch (tone) {
        case 'success':
            return 'alert-success';
        case 'warning':
            return 'alert-warning';
        case 'error':
            return 'alert-error';
        default:
            return 'alert-info';
    }
}

export function toneToBadgeClass(tone?: string) {
    switch (tone) {
        case 'success':
            return 'badge-success';
        case 'warning':
            return 'badge-warning';
        case 'error':
            return 'badge-error';
        case 'info':
            return 'badge-info';
        case 'secondary':
            return 'badge-secondary';
        default:
            return 'badge-neutral';
    }
}

export function responsiveSpan(
    value?: number | string | Record<string, number | string> | null
): string {
    const spanClassMap = {
        xs: {
            auto: 'col-auto',
            1: 'col-span-1',
            2: 'col-span-2',
            3: 'col-span-3',
            4: 'col-span-4',
            5: 'col-span-5',
            6: 'col-span-6',
            7: 'col-span-7',
            8: 'col-span-8',
            9: 'col-span-9',
            10: 'col-span-10',
            11: 'col-span-11',
            12: 'col-span-12',
        },
        sm: {
            auto: 'sm:col-auto',
            1: 'sm:col-span-1',
            2: 'sm:col-span-2',
            3: 'sm:col-span-3',
            4: 'sm:col-span-4',
            5: 'sm:col-span-5',
            6: 'sm:col-span-6',
            7: 'sm:col-span-7',
            8: 'sm:col-span-8',
            9: 'sm:col-span-9',
            10: 'sm:col-span-10',
            11: 'sm:col-span-11',
            12: 'sm:col-span-12',
        },
        md: {
            auto: 'md:col-auto',
            1: 'md:col-span-1',
            2: 'md:col-span-2',
            3: 'md:col-span-3',
            4: 'md:col-span-4',
            5: 'md:col-span-5',
            6: 'md:col-span-6',
            7: 'md:col-span-7',
            8: 'md:col-span-8',
            9: 'md:col-span-9',
            10: 'md:col-span-10',
            11: 'md:col-span-11',
            12: 'md:col-span-12',
        },
        lg: {
            auto: 'lg:col-auto',
            1: 'lg:col-span-1',
            2: 'lg:col-span-2',
            3: 'lg:col-span-3',
            4: 'lg:col-span-4',
            5: 'lg:col-span-5',
            6: 'lg:col-span-6',
            7: 'lg:col-span-7',
            8: 'lg:col-span-8',
            9: 'lg:col-span-9',
            10: 'lg:col-span-10',
            11: 'lg:col-span-11',
            12: 'lg:col-span-12',
        },
        xl: {
            auto: 'xl:col-auto',
            1: 'xl:col-span-1',
            2: 'xl:col-span-2',
            3: 'xl:col-span-3',
            4: 'xl:col-span-4',
            5: 'xl:col-span-5',
            6: 'xl:col-span-6',
            7: 'xl:col-span-7',
            8: 'xl:col-span-8',
            9: 'xl:col-span-9',
            10: 'xl:col-span-10',
            11: 'xl:col-span-11',
            12: 'xl:col-span-12',
        },
    } as const;

    const getSpanClass = (
        breakpoint: keyof typeof spanClassMap,
        span: number | string | undefined
    ) => {
        if (span === undefined || span === null) return '';
        return (
            spanClassMap[breakpoint][
                String(span) as keyof (typeof spanClassMap)[typeof breakpoint]
            ] || ''
        );
    };

    if (!value) return spanClassMap.xs[12];
    if (typeof value === 'number' || typeof value === 'string') {
        return getSpanClass('xs', value) || spanClassMap.xs[12];
    }

    return (
        [
            getSpanClass('xs', value.xs),
            getSpanClass('sm', value.sm),
            getSpanClass('md', value.md),
            getSpanClass('lg', value.lg),
            getSpanClass('xl', value.xl),
        ]
            .filter(Boolean)
            .join(' ') || spanClassMap.xs[12]
    );
}

export function responsiveDirection(
    value?: string | Record<string, string> | null,
    fallback: 'row' | 'column' = 'column'
) {
    const directionClassMap = {
        xs: {
            row: 'flex-row',
            column: 'flex-col',
        },
        sm: {
            row: 'sm:flex-row',
            column: 'sm:flex-col',
        },
        md: {
            row: 'md:flex-row',
            column: 'md:flex-col',
        },
        lg: {
            row: 'lg:flex-row',
            column: 'lg:flex-col',
        },
        xl: {
            row: 'xl:flex-row',
            column: 'xl:flex-col',
        },
    } as const;

    const getDirectionClass = (
        breakpoint: keyof typeof directionClassMap,
        direction: string | undefined
    ) => {
        if (!direction) return '';
        return (
            directionClassMap[breakpoint][
                direction as keyof (typeof directionClassMap)[typeof breakpoint]
            ] || ''
        );
    };

    if (!value) return directionClassMap.xs[fallback];
    if (typeof value === 'string') {
        return getDirectionClass('xs', value) || directionClassMap.xs[fallback];
    }

    return (
        [
            getDirectionClass('xs', value.xs),
            getDirectionClass('sm', value.sm),
            getDirectionClass('md', value.md),
            getDirectionClass('lg', value.lg),
            getDirectionClass('xl', value.xl),
        ]
            .filter(Boolean)
            .join(' ') || directionClassMap.xs[fallback]
    );
}

export function normalizeInputValue(value: unknown) {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return String(value);
    if (typeof value === 'boolean') return value ? 'true' : 'false';
    return '';
}
